/**
 * UnifiedTopBar [T115-F2/F3] [T116] [T117]
 *
 * Glass unified floating bar with always-on frosted glass design.
 * 4-row layout: Actions → Filters → Chips → Batch.
 * Uses IntersectionObserver sentinel for enhanced sticky state
 * and CSS transitions for batch action enter/exit.
 */

import { useRef, useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  RotateCcw,
  BarChart3,
  Layers,
  ListChecks,
  Download,
  Camera,
  Plus,
  X,
  Trash2,
  Wand2,
  Palette,
  Hash,
  ListRestart,
  Check,
  ChevronUp,
  ChevronDown,
  Menu,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { UnifiedFilterBar } from '@/components/transactions/UnifiedFilterBar';
import type { FilterState } from '@/lib/filters';
import TransactionColumnSelector, { TRANSACTION_COLUMNS } from '@/components/transactions/TransactionColumnSelector';
import type { TransactionColumnId } from '@/components/transactions/TransactionColumnSelector';
import type { Account, Category } from '@/lib/types';
import { useDraggableDropletX } from '@/hooks/useDraggableDropletX';

// ─── Types ─────────────────────────────────────────────────────────────────────

const GROUP_BY_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'date', label: 'Date' },
  { value: 'type', label: 'Type' },
  { value: 'category', label: 'Category' },
  { value: 'account', label: 'Account' },
] as const;

const MENU_SECTION_LABEL_CLASS = 'px-2 py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground';
const MENU_ACTION_ROW_CLASS = 'min-h-[44px] w-full gap-3 px-3 py-2 text-sm';
const MENU_OPTION_ROW_CLASS = 'min-h-[44px] w-full gap-3 py-2 pl-10 pr-2 text-sm';
const MENU_ICON_SLOT_CLASS = 'flex h-5 w-5 shrink-0 items-center justify-center [&_svg]:h-4 [&_svg]:w-4';

function SelectionModeSwitch({ compact, enabled }: { compact?: boolean; enabled: boolean }) {
  return (
    <span
      className={cn(
        'relative inline-flex shrink-0 items-center rounded-full border shadow-sm',
        compact
          ? enabled
            ? 'h-4 w-7 border-teal-600/60 bg-teal-500/35'
            : 'h-4 w-7 border-border/70 bg-muted/70'
          : enabled
            ? 'h-4 w-7 border-emerald-500/60 bg-emerald-500/25'
            : 'h-4 w-7 border-border/60 bg-muted/70',
      )}
      data-testid="selection-mode-state-indicator"
      data-state={enabled ? 'on' : 'off'}
      aria-hidden="true"
    >
      <span
        className={cn(
          'inline-block rounded-full shadow-sm transition-transform motion-reduce:transition-none motion-reduce:duration-0',
          compact ? 'h-2.5 w-2.5 bg-white' : 'h-2.5 w-2.5 bg-background/95',
          enabled
            ? compact ? 'translate-x-[16px]' : 'translate-x-[14px]'
            : 'translate-x-[2px]',
        )}
        data-testid="selection-mode-state-knob"
      />
    </span>
  );
}

// ─── Props ─────────────────────────────────────────────────────────────────────

export interface UnifiedTopBarProps {
  // Data
  totalCount: number;
  filters: FilterState;
  accounts: Account[];
  categories: Category[];
  /** All known tags for autocomplete */
  allTags?: string[];
  /** Tag stats with usage counts for tag suggestions */
  tagStats?: { tag: string; count: number }[];

  // View state (for active-state styling)
  showInsights: boolean;
  groupBy: string;
  selectionMode: boolean;
  onToggleSelectionMode: () => void;

  // Filter callbacks
  onFiltersChange: (f: FilterState) => void;
  onClear: () => void;

  // Action callbacks
  onRefresh: () => void;
  onExport: () => void;
  onScanReceipt: () => void;
  onAddTransaction: () => void;
  onToggleInsights?: () => void;
  onGroupByChange?: (value: string) => void;
  onResetView?: () => void;
  visibleDetails?: Record<TransactionColumnId, boolean>;
  onToggleVisibleDetail?: (id: TransactionColumnId) => void;
  onResetVisibleDetails?: () => void;
  showItemCount?: boolean;
  onShowItemCountChange?: (visible: boolean) => void;

  // Batch action props
  selectedCount: number;
  onEditCategory: () => void;
  onAddRemoveTags: () => void;
  onDelete: () => void;
  onApplyRule: () => void;
  onClearSelection: () => void;
  onStickyHeightChange?: (height: number) => void;

  /**
   * [AC7] Real available transaction date bounds. Computed by the page from
   * the loaded transaction list and threaded through to the date range control
   * so the All range can name its real Start/End dates.
   */
  transactionDateBounds?: { from?: Date; to?: Date };

  /** Override: render a custom export trigger instead of the default icon button */
  exportTrigger?: React.ReactNode;

  // [T-FLOATING-BAR-TOGGLE-PY] Full floating-bar visibility (page-owned persistence).
  isTopBarHidden: boolean;
  onToggleTopBarHidden: () => void;
}
// ─── Component ─────────────────────────────────────────────────────────────────

export function UnifiedTopBar({
  totalCount,
  filters,
  accounts,
  categories,
  allTags,
  tagStats,
  showInsights,
  groupBy,
  selectionMode,
  onToggleSelectionMode,
  onFiltersChange,
  onClear,
  onRefresh,
  onExport,
  onScanReceipt,
  onAddTransaction,
  onToggleInsights,
  onGroupByChange,
  onResetView,
  visibleDetails,
  onToggleVisibleDetail,
  onResetVisibleDetails,
  showItemCount,
  onShowItemCountChange,
  selectedCount,
  onEditCategory,
  onAddRemoveTags,
  onDelete,
  onApplyRule,
  onClearSelection,
  onStickyHeightChange,
  exportTrigger,
  isTopBarHidden,
  onToggleTopBarHidden,
  transactionDateBounds,
}: UnifiedTopBarProps) {
  // ── Sticky sentinel ────────────────────────────────────────────────────────
  const sentinelRef = useRef<HTMLDivElement>(null);
  const stickyRootRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!onStickyHeightChange) return;
    const root = stickyRootRef.current;
    if (!root) return;

    const emitHeight = () => {
      const nextHeight = Math.ceil(root.getBoundingClientRect().height);
      if (nextHeight > 0) onStickyHeightChange(nextHeight);
    };

    emitHeight();
    const observer = new ResizeObserver(emitHeight);
    observer.observe(root);
    window.addEventListener('resize', emitHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', emitHeight);
    };
  }, [onStickyHeightChange]);

  // ── Batch count change detection (for pulse animation) ─────────────────────
  const prevCountRef = useRef(selectedCount);
  const [countJustChanged, setCountJustChanged] = useState(false);

  useEffect(() => {
    if (selectedCount !== prevCountRef.current && selectedCount > 0) {
      setCountJustChanged(true);
      const timer = setTimeout(() => setCountJustChanged(false), 300);
      prevCountRef.current = selectedCount;
      return () => clearTimeout(timer);
    }
    prevCountRef.current = selectedCount;
  }, [selectedCount]);

  // ── Group By label ─────────────────────────────────────────────────────────

  const activeGroupLabel = GROUP_BY_OPTIONS.find((o) => o.value === groupBy)?.label ?? 'None';

  // ── Visible-details customisation ──────────────────────────────────────────
  // Mirrors TransactionColumnSelector.hasCustomisation: column visibility differs from the
  // per-column default OR item-count visibility was turned off. Used only inside the
  // minimized menu to gate the "visible-details-reset-btn" entry — the expanded toolbar's
  // TransactionColumnSelector owns its own identical check.
  const isVisibleDetailsCustomized = useMemo(() => {
    if (!visibleDetails) return false;
    const columnCustomisation = TRANSACTION_COLUMNS.some(
      (c) => (visibleDetails[c.id] ?? false) !== c.defaultVisible,
    );
    const itemCountCustomisation = typeof showItemCount === 'boolean' && showItemCount !== true;
    return columnCustomisation || itemCountCustomisation;
  }, [visibleDetails, showItemCount]);

  // ── Confirmation dialog state [T123] ────────────────────────────────────────
  const [showResetViewConfirm, setShowResetViewConfirm] = useState(false);
  // ── Draggable droplet [T-DRAGGABLE-BOTTOM-HANGING-FULLBAR-DROPLET-PY] ──────
  // Horizontal long-press drag (mirror of FAB lines 132-249 with a 300ms threshold)
  // so the bottom-hanging collapse/expand toggle can be repositioned. Y is anchored
  // to the floating bar's bottom edge by the parent layout (translate-y-50% via
  // the inline transform below) — only X is user-controlled.
  const {
    dx: dropletDx,
    isDragging: isDropletDragging,
    consumeHasDragged: consumeDropletDrag,
    handlePointerDown: handleDropletPointerDown,
  } = useDraggableDropletX({ anchor: 'right' });
  // [WP3] Expanded-strip visual height: 36px on desktop (h-9), 44px touch target on mobile via min-h override.

  const ACTION_TOUCH_UNIT_CLASS = 'min-h-[44px] min-w-[44px]';
  const stripButtonClass = 'h-9 min-h-[44px] md:min-h-0 gap-1.5 text-xs shrink-0';
  const isMinimized = isTopBarHidden;

  // [T-FLOATING-BAR-ANIM-PERF] Measured-height collapse: the filter row stays
  // mounted and we animate one explicit height value instead of interpolating
  // grid-template-rows. ResizeObserver keeps the height correct when filter
  // chips change size.
  const filterRowRef = useRef<HTMLDivElement>(null);
  const [filterRowHeight, setFilterRowHeight] = useState(0);
  useLayoutEffect(() => {
    const el = filterRowRef.current;
    if (!el) return;
    const update = () => setFilterRowHeight(el.scrollHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const minimizedActionsMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton
          tooltip="More transaction actions"
          tooltipSide="bottom"
          variant="ghost"
          size="icon"
          className={cn(
            'h-11 w-11 shrink-0 rounded-md border border-border/60 bg-card p-0 text-card-foreground shadow-sm hover:bg-accent data-[state=open]:bg-accent data-[state=open]:text-accent-foreground',
            ACTION_TOUCH_UNIT_CLASS,
          )}
          data-testid="minimized-actions-menu"
        >
          <Menu className="h-4 w-4" />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={4}
        collisionPadding={{ top: 64, right: 8, bottom: 96, left: 8 }}
        // Radix `available-height` lets the menu auto-shrink inside the viewport while the
        // explicit 28rem cap keeps the menu from dominating the screen. `overflow-y-auto` +
        // stable scrollbar gutter preserve keyboard scroll and avoid layout jumps when the
        // visible-detail list expands.
        className="z-[60] w-[min(20rem,calc(100vw-1rem))] min-w-0 sm:w-72 lg:w-80 max-h-[min(28rem,var(--radix-dropdown-menu-content-available-height))] max-[767px]:max-h-[18.1875rem] md:max-h-[26.4375rem] overflow-y-auto p-1 pb-3 motion-reduce:animate-none motion-reduce:duration-0 motion-reduce:transition-none motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=open]:duration-0 motion-reduce:data-[state=closed]:animate-none motion-reduce:data-[state=closed]:duration-0 [scrollbar-gutter:stable] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-muted-foreground/40 [&::-webkit-scrollbar-track]:bg-muted/20"
        data-testid="minimized-actions-menu-content"
      >
        {/* ─── View group ─── Refresh, Insights, Group By, optional visible-detail columns ─── */}
        <DropdownMenuLabel className={MENU_SECTION_LABEL_CLASS}>View</DropdownMenuLabel>
        <DropdownMenuItem
          className={MENU_ACTION_ROW_CLASS}
          onSelect={onRefresh}
        >
          <span className={MENU_ICON_SLOT_CLASS}>
            <RotateCcw />
          </span>
          Refresh
        </DropdownMenuItem>
        {onToggleInsights && (
          <DropdownMenuCheckboxItem
            className={MENU_OPTION_ROW_CLASS}
            checked={showInsights}
            aria-checked={showInsights}
            onCheckedChange={() => onToggleInsights()}
          >
            <span className={MENU_ICON_SLOT_CLASS}>
              <BarChart3 />
            </span>
            Insights
          </DropdownMenuCheckboxItem>
        )}
        {(onGroupByChange || (visibleDetails && onToggleVisibleDetail)) && (
          <>
            <DropdownMenuSeparator className="my-1" />
            <div
              className={cn(
                'grid min-w-0 gap-x-1',
                onGroupByChange && visibleDetails && onToggleVisibleDetail
                  ? 'grid-cols-2'
                  : 'grid-cols-1',
              )}
            >
              {onGroupByChange && (
                <div className="min-w-0">
                  <DropdownMenuLabel className={MENU_SECTION_LABEL_CLASS}>Group By</DropdownMenuLabel>
                  <DropdownMenuRadioGroup value={groupBy} onValueChange={onGroupByChange}>
                    {GROUP_BY_OPTIONS.map((option) => (
                      <DropdownMenuRadioItem
                        key={option.value}
                        className={MENU_OPTION_ROW_CLASS}
                        value={option.value}
                        data-testid={`minimized-group-by-${option.value}`}
                      >
                        {option.label}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </div>
              )}
              {visibleDetails && onToggleVisibleDetail && (
                <div className="min-w-0">
                  <DropdownMenuLabel
                    className={MENU_SECTION_LABEL_CLASS}
                    data-testid="minimized-visible-details-label"
                  >
                    Columns
                  </DropdownMenuLabel>
                  {TRANSACTION_COLUMNS.filter((col) => !col.required).map((col) => {
                    const checked = visibleDetails[col.id] ?? false;
                    return (
                      <DropdownMenuCheckboxItem
                        key={col.id}
                        className={MENU_OPTION_ROW_CLASS}
                        checked={checked}
                        aria-checked={checked}
                        onCheckedChange={() => onToggleVisibleDetail(col.id)}
                        onSelect={(e) => e.preventDefault()} // keep menu open while toggling
                        data-testid={`visible-detail-toggle-${col.id}`}
                      >
                        {col.detailLabel ?? col.label}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
                  {typeof showItemCount === 'boolean' && onShowItemCountChange && (
                    <DropdownMenuCheckboxItem
                      className={MENU_OPTION_ROW_CLASS}
                      checked={showItemCount}
                      aria-checked={showItemCount}
                      onCheckedChange={(checked) => onShowItemCountChange(checked === true)}
                      onSelect={(e) => e.preventDefault()}
                      data-testid="visible-detail-toggle-item-count"
                    >
                      Item count
                    </DropdownMenuCheckboxItem>
                  )}
                  {onResetVisibleDetails && isVisibleDetailsCustomized && (
                    <>
                      <DropdownMenuSeparator className="my-1" />
                      <DropdownMenuItem
                        className={`${MENU_ACTION_ROW_CLASS} text-muted-foreground`}
                        onSelect={() => onResetVisibleDetails()}
                        data-testid="visible-details-reset-btn"
                      >
                        <span className={MENU_ICON_SLOT_CLASS}>
                          <ListRestart />
                        </span>
                        Reset to default
                      </DropdownMenuItem>
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* ─── Capture and export group ─── Scan receipt, Export ─── */}
        <DropdownMenuSeparator className="my-1" />
        <DropdownMenuLabel className={MENU_SECTION_LABEL_CLASS}>Capture and export</DropdownMenuLabel>
        <DropdownMenuItem
          className={MENU_ACTION_ROW_CLASS}
          onSelect={onScanReceipt}
        >
          <span className={MENU_ICON_SLOT_CLASS}>
            <Camera />
          </span>
          Scan receipt
        </DropdownMenuItem>
        {exportTrigger ? (
          <div className="min-h-[44px] w-full px-1 py-1" data-testid="minimized-menu-export">
            {exportTrigger}
          </div>
        ) : (
          <DropdownMenuItem
            className={MENU_ACTION_ROW_CLASS}
            onSelect={onExport}
          >
            <span className={MENU_ICON_SLOT_CLASS}>
              <Download />
            </span>
            Export
          </DropdownMenuItem>
        )}

        {/* ─── Reset group ─── Reset View (destructive confirm) ─── */}
        {onResetView && (
          <>
            <DropdownMenuSeparator className="my-1" />
            <DropdownMenuLabel className={MENU_SECTION_LABEL_CLASS}>Reset</DropdownMenuLabel>
            <DropdownMenuItem
              className={`${MENU_ACTION_ROW_CLASS} text-foreground focus:bg-accent focus:text-accent-foreground`}
              onSelect={() => setShowResetViewConfirm(true)}
            >
              <span className={MENU_ICON_SLOT_CLASS}>
                <ListRestart />
              </span>
              Reset View
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const expandedTransactionCount = (
    <Badge
      variant="secondary"
      className="h-9 min-h-[44px] min-w-[80px] shrink-0 items-center justify-center rounded-md border border-border/60 bg-card px-2 text-xs font-medium tabular-nums md:min-h-0"
      data-testid="transaction-count-badge"
    >
      {totalCount.toLocaleString()} txn
    </Badge>
  );

  const expandedTransactionSelection = (
    <Button
      variant={selectionMode ? 'secondary' : 'ghost'}
      size="sm"
      className={cn(
        'box-border inline-flex h-9 flex-[0_0_auto] items-center justify-start gap-0 rounded-md border border-border/60 bg-card px-2.5 text-card-foreground',
        ACTION_TOUCH_UNIT_CLASS,
        'transition-[background-color,box-shadow,transform] duration-150 ease-out motion-reduce:transition-none motion-reduce:duration-0',
        'hover:bg-accent/60 active:scale-[0.98] active:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        'data-[state=on]:border-primary/60 data-[state=on]:bg-primary/10',
        selectionMode && 'font-medium shadow-sm',
      )}
      onClick={onToggleSelectionMode}
      role="switch"
      aria-checked={selectionMode}
      aria-label="Transaction selection mode"
      data-state={selectionMode ? 'on' : 'off'}
      data-testid="selection-mode-toggle"
    >
      {selectionMode ? (
        <Check className="h-4 w-4 shrink-0 text-foreground" data-testid="collapsed-selection-check" />
      ) : (
        <ListChecks className="h-4 w-4 shrink-0 text-foreground" data-testid="collapsed-selection-check" />
      )}
      <SelectionModeSwitch enabled={selectionMode} compact />
      <span className="sr-only" data-testid="selection-mode-state-text">
        {selectionMode ? 'Selection mode on' : 'Selection mode off'}
      </span>
    </Button>
  );

  const minimizedActions = (
    <>
      <div
        // [WP5] Shared count-chip: rounded-md (local --radius), visible border-border/60, bg-card, tabular-nums, 44px hit area, 56px min width for 4-digit counts.

        className="box-border inline-flex h-11 w-14 min-h-[44px] min-w-0 flex-[0_0_56px] shrink-0 items-center justify-center overflow-hidden whitespace-nowrap rounded-md border border-border/60 bg-card px-0 text-[10px] font-medium tabular-nums leading-none text-card-foreground lg:w-20 lg:min-w-[80px] lg:flex-[0_0_80px] lg:px-2 lg:text-xs"
        data-testid="transaction-count-badge"
      >
        {totalCount.toLocaleString()} txn
      </div>
      <UnifiedFilterBar
        presentation="minimized-inline"
        filters={filters}
        onFiltersChange={onFiltersChange}
        onClear={onClear}
        accounts={accounts}
        categories={categories}
        allTags={allTags}
        tagStats={tagStats}
        transactionDateBounds={transactionDateBounds}
      />
      <Button
        variant={selectionMode ? 'secondary' : 'ghost'}
        size="sm"
        className={cn(
          'box-border inline-flex h-11 flex-[0_0_auto] items-center justify-start gap-0 rounded-md border border-border/60 bg-card px-2.5 text-card-foreground transition-[background-color,box-shadow,transform] duration-150 ease-out motion-reduce:transition-none motion-reduce:duration-0 hover:bg-accent/60 active:scale-[0.98] active:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 data-[state=on]:border-primary/60 data-[state=on]:bg-primary/10',
          ACTION_TOUCH_UNIT_CLASS,
        )}
        onClick={onToggleSelectionMode}
        role="switch"
        aria-checked={selectionMode}
        aria-label="Transaction selection mode"
        data-state={selectionMode ? 'on' : 'off'}
        data-testid="selection-mode-toggle"
      >
        <Check className="h-4 w-4 shrink-0 text-foreground" data-testid="collapsed-selection-check" />
        <SelectionModeSwitch enabled={selectionMode} compact />
        <span className="sr-only" data-testid="selection-mode-state-text">
          {selectionMode ? 'Selection mode on' : 'Selection mode off'}
        </span>
      </Button>
      <Button
        size="icon"
        variant="ghost"
        // [WP7] Semantic primary tokens — was amber-500 + text-foreground. Light text on amber has poor contrast in dark mode where --foreground is near-white. bg-primary/text-primary-foreground stays inside the brand color pair used by the rest of the bar.
        className={cn(
          'h-11 w-11 shrink-0 rounded-md border border-primary/60 bg-primary p-0 text-primary-foreground shadow-sm hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          ACTION_TOUCH_UNIT_CLASS,
        )}

        onClick={onAddTransaction}
        data-testid="add-transaction-btn"
        aria-label="Add Transaction"
        title="Add Transaction (Ctrl+N)"
      >
        <Plus className="h-4 w-4" />
      </Button>
      {minimizedActionsMenu}
    </>
  );

  return (
    <>
      {/* Zero-height sentinel for IntersectionObserver */}
      <div ref={sentinelRef} className="h-0 w-full" aria-hidden="true" />

      <div
        ref={stickyRootRef}
        data-stuck={isStuck ? 'true' : undefined}
        data-testid="unified-topbar"
        className={cn(
          // Always-on glass container — edge-to-edge, flush with header
          'relative sticky -top-4 md:-top-6 z-[8] pt-4 md:pt-6 pb-2.5 transition-all duration-200',
          '-mx-4 md:-mx-6 px-4 md:px-6',
          'bg-background/90 supports-[backdrop-filter]:bg-background/80 backdrop-blur-md',
          'border-b border-border/40',
          'shadow-sm',
          'after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-border/50',
          // Enhanced glass when sticky
          isStuck && 'shadow-md border-border/60 supports-[backdrop-filter]:bg-background/86',
          // [T-TRENDS-V2-RANGE-MATRIX] Stop the decorative wrapper from intercepting
          // clicks on content scrolling beneath it. Transparent/blank areas must let
          // pointer events pass through; only real interactive descendants opt back in.
          'pointer-events-none',
          '[&_button]:pointer-events-auto',
          '[&_a]:pointer-events-auto',
          '[&_input]:pointer-events-auto',
          '[&_select]:pointer-events-auto',
          '[&_textarea]:pointer-events-auto',
          '[&_[role=button]]:pointer-events-auto',
          '[&_[role=switch]]:pointer-events-auto',
          '[&_[role=checkbox]]:pointer-events-auto',
          '[&_[role=radio]]:pointer-events-auto',
          '[&_[role=combobox]]:pointer-events-auto',
          '[&_[role=spinbutton]]:pointer-events-auto',
          // [WP8] Constrain the expanded date-range trigger to its flex slot so its
          // intrinsic inline-flex width cannot overflow into the 96x44 selection pill.
          '[&_[data-testid=transaction-date-range-trigger]]:w-full',
          '[&_[data-testid=transaction-date-range-trigger]]:min-w-0',
          '[&_[data-testid=transaction-date-range-trigger]]:overflow-hidden',
          '[&_[data-testid=transaction-date-range-trigger]>span]:min-w-0',
          '[&_[data-testid=transaction-date-range-trigger]>span]:shrink',
          '[&_[data-testid=transaction-date-range-trigger]>span]:overflow-hidden',
          '[&_[data-testid=transaction-date-range-trigger]>span]:text-ellipsis',
        )}
      >
        <div
          id="floating-bar-actions"
          data-testid="floating-bar-actions"
          className={cn(
            ACTION_TOUCH_UNIT_CLASS,
            'flex w-full min-w-0 items-center pointer-events-auto',
            isMinimized
              ? 'gap-1 overflow-x-auto overscroll-x-contain scrollbar-none'
              : 'gap-1.5 overflow-visible',
          )}
        >
          {isMinimized ? minimizedActions : (
          <>
          <div
            className="transactions-toolbar-scroll flex min-w-0 flex-1 items-center gap-1 overflow-x-auto overscroll-x-contain scrollbar-none rounded-md border border-border/40 bg-background/45 px-1 py-1 shadow-inner"
            data-testid="transactions-toolbar-strip"
            role="toolbar"
            aria-label="Transactions actions"
          >
              <Button
                variant="ghost"
                size="sm"
                className={stripButtonClass}
                onClick={onRefresh}
                data-testid="refresh-btn"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Refresh
              </Button>

              {onToggleInsights && (
                <Button
                  variant={showInsights ? 'secondary' : 'ghost'}
                  size="sm"
                  className={stripButtonClass}
                  onClick={onToggleInsights}
                  data-testid="insights-btn"
                >
                  <BarChart3 className="h-3.5 w-3.5" />
                  Insights
                </Button>
              )}

              {onGroupByChange && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant={groupBy !== 'none' ? 'secondary' : 'ghost'}
                      size="sm"
                      className={stripButtonClass}
                      data-testid="group-by-btn"
                    >
                      <Layers className="h-3.5 w-3.5" />
                      Group: {activeGroupLabel}
                      <ChevronDown className="h-3 w-3 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" collisionPadding={8} forceMount data-testid="group-by-dropdown-content">
                    {GROUP_BY_OPTIONS.map((opt) => (
                      <DropdownMenuCheckboxItem
                        key={opt.value}
                        checked={groupBy === opt.value}
                        onCheckedChange={() => onGroupByChange(opt.value)}
                        data-testid={`group-by-opt-${opt.value}`}
                      >
                        {opt.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {visibleDetails && onToggleVisibleDetail && onResetVisibleDetails && (
                <TransactionColumnSelector
                  visibility={visibleDetails}
                  onToggle={onToggleVisibleDetail}
                  onReset={onResetVisibleDetails}
                  itemCountVisible={showItemCount}
                  onItemCountChange={onShowItemCountChange}
                  triggerClassName={stripButtonClass}
                  align="start"
                />
              )}

              <Separator orientation="vertical" className="h-5 mx-1 shrink-0" />

              <Button
                variant="ghost"
                size="sm"
                className={stripButtonClass}
                onClick={onScanReceipt}
                data-testid="scan-receipt-btn"
              >
                <Camera className="h-3.5 w-3.5" />
                Scan
              </Button>

              <span className="shrink-0">
                {exportTrigger ?? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className={stripButtonClass}
                    onClick={onExport}
                    data-testid="export-btn"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Export
                  </Button>
                )}
              </span>

              {onResetView && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={`${stripButtonClass} text-destructive hover:text-destructive`}
                  onClick={() => setShowResetViewConfirm(true)}
                  data-testid="reset-view-btn"
                >
                  <ListRestart className="h-3.5 w-3.5" />
                  Reset View
                </Button>
              )}
          </div>

          <Button
            size="sm"
            className="h-9 min-h-[44px] md:min-h-0 px-3 text-xs gap-1.5 shrink-0"
            onClick={onAddTransaction}
            data-testid="add-transaction-btn"
            aria-label="Add Transaction"
            title="Add Transaction (Ctrl+N)"
          >
            <Plus className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Add</span>
          </Button>

          </>
          )}
        </div>

        <div
          id="floating-bar-content"
          data-testid="floating-bar-content"
          aria-hidden={isTopBarHidden}
          className={cn(
            // [T-FLOATING-BAR-ANIM-PERF] Explicit height+opacity transition — no
            // grid-track interpolation, no transition-all. Height comes from a
            // ResizeObserver-measured inner row; motion-reduce users get no motion.
            'overflow-hidden transition-[height,opacity] duration-300 ease-in-out motion-reduce:transition-none',
            !isTopBarHidden ? 'opacity-100' : 'opacity-0',
          )}
          style={{ height: !isTopBarHidden ? filterRowHeight : 0 }}
        >
          <div ref={filterRowRef} className="min-h-0">
        {/* ═══ Row 2: Filters — always mounted; visibility controlled by #floating-bar-content height ═══ */}
        <div data-testid="transactions-filter-bar-row" className="overflow-hidden min-h-0">
          <div className="flex items-center gap-1.5">
            <div className="flex-1 min-w-0">
              <UnifiedFilterBar
                filters={filters}
                onFiltersChange={onFiltersChange}
                onClear={onClear}
                accounts={accounts}
                categories={categories}
                allTags={allTags}
                tagStats={tagStats}
                expandedCount={expandedTransactionCount}
                expandedActions={expandedTransactionSelection}
                transactionDateBounds={transactionDateBounds}
              />
            </div>
          </div>
        </div>
        {/* ═══ Row 3: Batch Actions — always rendered, CSS transitioned, hidden on mobile ═══ */}
        <div
          className={cn(
            'hidden md:block overflow-hidden transition-all ease-out',
            selectedCount > 0
              ? 'max-h-14 opacity-100 duration-300 pt-2 border-t border-border/30'
              : 'max-h-0 opacity-0 duration-200 pt-0',
          )}
          role="toolbar"
          aria-label="Batch actions"
          aria-hidden={selectedCount === 0}
          data-testid="batch-actions-desktop"
        >
          <div className="hidden md:flex items-center justify-between">
            {/* Left: selection count */}
            <div className="flex items-center gap-2">
              <Badge
                variant="default"
                className={cn(
                  'font-medium transition-transform duration-150',
                  countJustChanged && 'motion-safe:animate-pulse-scale',
                )}
                data-testid="batch-selection-count"
              >
                {selectedCount} selected
              </Badge>
            </div>
            {/* Center-right: action buttons */}
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                onClick={onEditCategory}
                data-testid="batch-categorize-btn"
              >
                <Palette className="h-3.5 w-3.5" /> Categorize
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                onClick={onAddRemoveTags}
                data-testid="batch-tags-btn"
              >
                <Hash className="h-3.5 w-3.5" /> Tags
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                onClick={onApplyRule}
                data-testid="batch-apply-rule-btn"
              >
                <Wand2 className="h-3.5 w-3.5" /> Apply Rule
              </Button>
              <Separator orientation="vertical" className="h-5 mx-1" />
              <IconButton
                tooltip="Delete selected"
                variant="destructive"
                size="sm"
                className="h-8 w-8"
                onClick={onDelete}
                data-testid="batch-delete-btn"
              >
                <Trash2 className="h-4 w-4" />
              </IconButton>
            </div>
            {/* Right: clear selection */}
            <IconButton
              tooltip="Clear selection"
              variant="ghost"
              size="sm"
              className="h-8 w-8"
              onClick={onClearSelection}
            >
              <X className="h-4 w-4" />
            </IconButton>
          </div>
        </div>
          </div>{/* close floating-bar-content overflow-hidden */}
        </div>{/* close #floating-bar-content wrapper */}

        {/* [T-FLOATING-BAR-COLLAPSE-TOGGLE-SURFACE-PY] Persistent right-aligned circular collapse-toggle —
            rendered as a direct DOM child of [data-testid="unified-topbar"] (NOT a portal). Positioned via
            the topbar's own layout: absolute + bottom:0 + translateY(50%) so the circle's center sits on
            the unified-topbar bottom edge. Surface-blended (bg-background/95 + 4px ring of background/90
            + subtle border + shadow) so it reads as part of the bar, not a detached overlay. The inline
            transform is X-only translate3d(dropletDx px, 50%, 0) driven by useDraggableDropletX. */}
        <IconButton
          tooltip={isTopBarHidden ? 'Restore floating bar' : 'Collapse floating bar'}
          tooltipSide="left"
          variant="ghost"
          size="icon"
          className={cn(
            // [WP7] Surface-blended circular toggle — bg-background/95 + ring of background/90 reads as
            // part of the unified-topbar surface (not a detached bright-sky square). The 4px halo ring
            // matches the bar's effective background so the join is a soft cutout rather than a hard edge.
            'min-h-[44px] min-w-[44px] touch-pan-y rounded-full border border-border/60 bg-background/95 text-foreground ring-4 ring-background/90 shadow-sm backdrop-blur-md hover:bg-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            'motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out motion-reduce:transition-none',
            // Drag affordance mirrors the + FAB: scale + ring + soft pulse + cursor + no text-selection.
            isDropletDragging && 'touch-none select-none cursor-grabbing shadow-md ring-1 ring-ring/40 scale-[1.04] motion-safe:animate-pulse',
          )}
          style={{
            position: 'absolute',
            bottom: 0,
            right: 12,
            transform: `translate3d(${dropletDx}px, 50%, 0)`,
            zIndex: 10,
          }}
          onPointerDown={handleDropletPointerDown}
          onClick={() => {
            if (consumeDropletDrag()) return;
            onToggleTopBarHidden();
          }}
          aria-label={isTopBarHidden ? 'Restore floating bar' : 'Collapse floating bar'}
          aria-expanded={!isTopBarHidden}
          aria-controls="floating-bar-content"
          data-testid="floating-bar-collapse-toggle"
        >
          <ChevronUp className={cn('h-4 w-4 transition-transform motion-safe:duration-200 motion-safe:ease-out', isTopBarHidden && 'rotate-180')} />
        </IconButton>
      </div>

      {/* [T123] Reset View confirmation */}
      <AlertDialog open={showResetViewConfirm} onOpenChange={setShowResetViewConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset view to defaults?</AlertDialogTitle>
            <AlertDialogDescription>
                This will reset all filters, grouping, and visible details to their defaults. Your data won't be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onResetView?.();
                setShowResetViewConfirm(false);
              }}
            >
              Reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
